import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Doctor, DoctorForm, DoctorSearchForm } from 'entities/doctor'

type DoctorsResponse = PagingResponse & {
    results: Doctor[]
}

export function useFetchDoctorsApi(searchForm: Form<DoctorSearchForm>): IndexApiSet<DoctorsResponse> & { execute: () => void } {
    const apiPath = 'doctors/'
    const api = useIndexApi<DoctorsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvDoctorsApi(searchForm: Form<DoctorSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'doctors/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'doctor', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostDoctorApi(): ApiSet<BaseResponse> & { execute: (form: Form<DoctorForm>) => void } {
    const apiSet = usePostApi<BaseResponse, DoctorForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<DoctorForm>) => {
        const apiPath = `doctors/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutDoctorApi(): ApiSet<BaseResponse> & { execute: (form: Form<DoctorForm>) => void } {
    const apiSet = usePutApi<BaseResponse, DoctorForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<DoctorForm>) => {
        const apiPath = `doctors/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutDoctorsApi(): ApiSet<BaseResponse> & { execute: (form: Form<DoctorForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, DoctorForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<DoctorForm[]>) => {
        const apiPath = `doctors/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteDoctorApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `doctors/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
